import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  TemplateRef,
  ViewContainerRef,
  effect,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { EmailManagementApiService } from '@anduin/api';
import { FirmPortalPermission, PermissionsState } from '@anduin/auth';
import { EmailDeliveryStep, EmailDeliveryTrackService } from '../../tracking';
import {
  AgGridSimpleDateFilter,
  AgGridStateService,
  ButtonDirective,
  DataSelectorDirective,
  extractFailureContext,
  getExportConfigs,
  IconComponent,
  MessageService,
  showOverlayOnZeroState,
  TooltipDirective,
  WhereAggregateOperator,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { ColDef, ColumnApi, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { Apollo } from 'apollo-angular';
import { EMPTY, firstValueFrom, interval } from 'rxjs';
import { catchError, delay, filter, switchMap, take, tap } from 'rxjs/operators';
import { EmailDeliveryHubState, LoadSettings } from '../../state';
import { EmailDeliveryHubExportConfig } from '../email-delivery-hub-sent-table/email-delivery-hub-export-config';
import { ResendEmailModalComponent } from '../resend-email-modal/resend-email-modal.component';
import { getReasonCodesMatchingSearch, isRetryable } from '../../utils/unsent-email-reason.utils';
import { buildRequestSnapshot, TableRequestSnapshot } from '../../utils';
import { EmailDeliveryHubFailedColumnBuilder } from './email-delivery-hub-failed-column-builder';
import { FailedEmailDatasource } from './services/failed-datasource.service';
import { FailedEmailExportDatasource } from './services/failed-export-datasource.service';
import { FailedGridOptionsFactory } from './services/failed-grid-options.factory';
import { FAILED_HASURA_BASE_QUERY_TOKEN } from './utils/hasura-base-query';
import { FailedEmailRow, FailedEmailStatus } from '../../models';

@Component({
  selector: 'app-email-delivery-hub-failed',
  templateUrl: './email-delivery-hub-failed.component.html',
  styleUrls: ['./email-delivery-hub-failed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridModule, ButtonDirective, DataSelectorDirective, IconComponent, MatMenu, MatMenuItem, MatMenuTrigger, TooltipDirective],
  host: { class: 'd-flex flex-column gap-4 flex-fill-container-y' },
})
export class EmailDeliveryHubFailedComponent {
  private readonly emailManagementApi = inject(EmailManagementApiService);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly dialog = inject(MatDialog);
  private readonly columnBuilder = inject(EmailDeliveryHubFailedColumnBuilder);
  private readonly datasource = inject(FailedEmailDatasource);
  private readonly gridOptionsFactory = inject(FailedGridOptionsFactory);
  private readonly apollo = inject(Apollo);
  private readonly exportBaseQuery = inject(FAILED_HASURA_BASE_QUERY_TOKEN);
  private readonly destroyRef = inject(DestroyRef);
  private readonly trackService = inject(EmailDeliveryTrackService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private readonly agGrid = viewChild.required(AgGridAngular);

  private readonly actionsTemplateRef = viewChild.required<TemplateRef<unknown>>('actionsTemplate');
  private readonly reasonFilterLabelRef = viewChild.required<TemplateRef<unknown>>('reasonFilterLabel');

  private readonly filters = this.store.selectSignal(EmailDeliveryHubState.getFailedFilters);
  private readonly settingsData = this.store.selectSignal(EmailDeliveryHubState.getSettings);

  readonly canResendEmails = this.store.selectSignal(PermissionsState.hasPermission([FirmPortalPermission.EMAIL_MANAGEMENT_RESEND_EMAILS]));

  readonly columnDefs = signal<ColDef<FailedEmailRow>[]>([]);

  readonly countChange = output<number>();
  readonly gridApiReady = output<GridApi>();
  readonly exportConfigReady = output<EmailDeliveryHubExportConfig>();

  readonly isRetryable = isRetryable;
  readonly isDismissed = (status: FailedEmailStatus): boolean => status === FailedEmailStatus.DISMISSED;
  readonly isActionDisabled = (status: FailedEmailStatus): boolean =>
    status === FailedEmailStatus.RETRYING || status === FailedEmailStatus.RESOLVED || status === FailedEmailStatus.DISMISSED;

  private readonly gridApi = signal<GridApi | undefined>(undefined);
  private readonly columnApi = signal<ColumnApi | undefined>(undefined);
  private agGridStateService: AgGridStateService;
  private datasourceSet = false;

  readonly loadingRowId = signal<number | null>(null);
  readonly isRowLoading = (id: number): boolean => this.loadingRowId() === id;

  readonly gridOptions: GridOptions;
  private readonly exportGridOptions: GridOptions;
  private readonly createExportDatasource = (): FailedEmailExportDatasource =>
    new FailedEmailExportDatasource(this.apollo, this.exportBaseQuery, this.createExportRequestSnapshot());

  constructor() {
    this.store.dispatch(new LoadSettings());

    this.gridOptions = this.gridOptionsFactory.create({
      onGridReadyCallback: (event: GridReadyEvent) => {
        this.gridApi.set(event.api);
        this.columnApi.set(event.columnApi);
        this.gridApiReady.emit(event.api);
        this.initAgGridStateService();
      },
      getGridDataStatus: () => this.datasource.requestStatus,
      isExternalFilterPresent: () => this.datasource.externalFilters.size > 0,
    });

    this.exportGridOptions = this.gridOptionsFactory.createExportGridOptions();

    // Emitted from an effect (not gridReady) because the column defs arrive in a later effect run
    effect(() => {
      const gridApi = this.gridApi();
      const columnApi = this.columnApi();
      const columnDefs = this.columnDefs();
      if (!gridApi || !columnApi || columnDefs.length === 0) return;

      this.exportConfigReady.emit({
        tableId: 'emailDeliveryHubFailed',
        gridApi,
        columnApi,
        defaultColumnDef: this.exportGridOptions.defaultColDef,
        columnDefs,
        gridOptions: this.exportGridOptions,
        externalFilters: this.datasource.externalFilters,
        createDatasource: this.createExportDatasource,
        getExportParams: getExportConfigs,
      });
    });

    // Update overlay on data status changes
    this.datasource.requestStatus$
      .pipe(
        delay(250),
        tap(() => showOverlayOnZeroState(this.gridApi())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Build column defs once template refs are available
    effect(() => {
      this.columnDefs.set(this.columnBuilder.buildFailedColumns(this.actionsTemplateRef(), this.reasonFilterLabelRef()));
    });

    // Load initial undismissed count
    this.refreshCount();

    // Poll RETRYING rows every 30s; refresh grid when any transition out of RETRYING
    interval(30_000)
      .pipe(
        switchMap(() => {
          const retryingIds = this.getRetryingRowIds();
          if (retryingIds.length === 0) return EMPTY;
          return this.datasource.pollRetryingStatuses(retryingIds);
        }),
        filter((results) => results.some((r) => r.status !== FailedEmailStatus.RETRYING)),
        tap(() => this.refreshGrid()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // React to filter changes
    effect(() => {
      const currentFilters = this.filters();
      const api = this.gridApi();
      if (!api) return;

      this.applyFilters(currentFilters);
      this.setDatasourceOrRefresh(api);
    });
  }

  async dismiss(row: FailedEmailRow): Promise<void> {
    this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_CLICKED, {
      action: 'DISMISS',
      unsentEmailId: row.id,
    });

    const confirmed = await this.messageService.confirmAsync({
      title: 'Dismiss Email',
      message: 'This will dismiss the failed email, removing it from your active queue. You can restore it later if needed.',
      confirmButtonText: 'Dismiss',
      confirmButtonColor: 'negative',
    });
    if (!confirmed) {
      this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_DISMISSED, {
        action: 'DISMISS',
        unsentEmailId: row.id,
      });
      return;
    }
    const startedAt = performance.now();
    this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_SUBMITTED, {
      action: 'DISMISS',
      unsentEmailId: row.id,
    });

    this.emailManagementApi
      .dismissUnsentEmail(row.id)
      .pipe(
        tap(() => this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_SUCCEEDED, {
          action: 'DISMISS',
          unsentEmailId: row.id,
          operationDurationMs: performance.now() - startedAt,
        })),
        tap(() => this.messageService.success('Email record dismissed successfully.')),
        tap(() => this.refreshGridIfEnabled()),
        catchError((error) => {
          this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_FAILED, {
            action: 'DISMISS',
            unsentEmailId: row.id,
            ...extractFailureContext(error),
            operationDurationMs: performance.now() - startedAt,
          });
          this.messageService.error('Failed to dismiss the email record. Please try again.');
          return EMPTY;
        }),
      )
      .subscribe();
  }

  async undismiss(row: FailedEmailRow): Promise<void> {
    this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_CLICKED, {
      action: 'UNDISMISS',
      unsentEmailId: row.id,
    });

    const confirmed = await this.messageService.confirmAsync({
      title: 'Restore Email',
      message: `This will restore the email to your active failed queue so it can be reviewed${isRetryable(row.reasonCode) ? ' or retried' : ''}.`,
      confirmButtonText: 'Restore',
      confirmButtonColor: 'primary',
    });
    if (!confirmed) {
      this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_DISMISSED, {
        action: 'UNDISMISS',
        unsentEmailId: row.id,
      });
      return;
    }
    const startedAt = performance.now();
    this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_SUBMITTED, {
      action: 'UNDISMISS',
      unsentEmailId: row.id,
    });

    this.emailManagementApi
      .undismissUnsentEmail(row.id)
      .pipe(
        tap(() => this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_SUCCEEDED, {
          action: 'UNDISMISS',
          unsentEmailId: row.id,
          operationDurationMs: performance.now() - startedAt,
        })),
        tap(() => this.messageService.success('Email record restored successfully.')),
        tap(() => this.refreshGridIfEnabled()),
        catchError((error) => {
          this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_FAILED, {
            action: 'UNDISMISS',
            unsentEmailId: row.id,
            ...extractFailureContext(error),
            operationDurationMs: performance.now() - startedAt,
          });
          this.messageService.error('Failed to restore the email record. Please try again.');
          return EMPTY;
        }),
      )
      .subscribe();
  }

  async retry(row: FailedEmailRow): Promise<void> {
    this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_CLICKED, {
      action: 'RETRY',
      unsentEmailId: row.id,
    });

    if (row.reasonCode === 'NO_RECIPIENT') {
      this.dialog
        .open(ResendEmailModalComponent, {
          data: {
            mode: 'retry' as const,
            recipients: [
              ...this.parseRecipientString(row.recipient, 'to'),
              ...this.parseRecipientString(row.recipientCc, 'cc'),
              ...this.parseRecipientString(row.recipientBcc, 'bcc'),
            ],
            unsentEmailId: row.id,
            canEditRecipients: true,
            tracking: { unsentEmailId: row.id },
          },
          viewContainerRef: this.viewContainerRef,
        })
        .afterClosed()
        .pipe(
          take(1),
          filter(result => result?.success === true),
          tap(() => this.messageService.success('Email record queued for retry.')),
          tap(() => this.refreshGridIfEnabled()),
        )
        .subscribe();
    } else {
      const confirmed = await this.messageService.confirmAsync({
        title: 'Retry Email',
        message: 'This will attempt to resend the email using the original recipient and message details.'
          + ' The email status will update automatically once the attempt completes.',
        confirmButtonText: 'Retry',
        confirmButtonColor: 'primary',
      });
      if (!confirmed) {
        this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_DISMISSED, {
          action: 'RETRY',
          unsentEmailId: row.id,
        });
        return;
      }
      const startedAt = performance.now();
      this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_SUBMITTED, {
        action: 'RETRY',
        unsentEmailId: row.id,
      });

      this.emailManagementApi
        .retryUnsentEmail(row.id)
        .pipe(
          tap(() => this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_SUCCEEDED, {
            action: 'RETRY',
            unsentEmailId: row.id,
            operationDurationMs: performance.now() - startedAt,
          })),
          tap(() => this.messageService.success('Email record queued for retry.')),
          tap(() => this.refreshGridIfEnabled()),
          catchError((error) => {
            this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_FAILED, {
              action: 'RETRY',
              unsentEmailId: row.id,
              ...extractFailureContext(error),
              operationDurationMs: performance.now() - startedAt,
            });
            this.messageService.error('Failed to retry the email record. Please try again.');
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  private initAgGridStateService(): void {
    this.agGridStateService = new AgGridStateService(
      { tableName: 'emailDeliveryHubFailed', rememberFilterModel: true },
      this.store,
      this.agGrid(),
    );
    this.agGridStateService
      .getAllEventListeners()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private applyFilters(filters: { dateRange?: { min?: string; max?: string }; search: string; showDismissedAndResolved: boolean }): void {
    // showDismissedAndResolved
    if (!filters.showDismissedAndResolved) {
      this.datasource.addExternalFilter('dismissedFilter', {
        operator: WhereAggregateOperator.AND,
        conditions: [
          { column: 'dismissedAt', filter: { filterType: 'date', type: 'isNull' } },
          { column: 'resolvedAt', filter: { filterType: 'date', type: 'isNull' } },
        ],
      });
    } else {
      this.datasource.removeExternalFilter('dismissedFilter');
    }

    // Date range
    if (filters.dateRange?.min && filters.dateRange?.max) {
      this.datasource.addExternalFilter('dateRange', {
        column: 'createdAt',
        filter: {
          filterType: 'date',
          type: 'inRange',
          filter: String(filters.dateRange.min),
          filterTo: String(filters.dateRange.max),
        } as AgGridSimpleDateFilter,
      });
    } else if (filters.dateRange?.min) {
      this.datasource.addExternalFilter('dateRange', {
        column: 'createdAt',
        filter: { filterType: 'date', type: 'greaterThanOrEqual', filter: String(filters.dateRange.min) },
      });
    } else if (filters.dateRange?.max) {
      this.datasource.addExternalFilter('dateRange', {
        column: 'createdAt',
        filter: { filterType: 'date', type: 'lessThanOrEqual', filter: String(filters.dateRange.max) },
      });
    } else {
      this.datasource.removeExternalFilter('dateRange');
    }

    // Search
    if (filters.search) {
      const matchingReasonCodes = getReasonCodesMatchingSearch(filters.search);
      const searchConditions = [
        { column: 'subject', filter: { filterType: 'text', type: 'contains', filter: filters.search } },
        { column: 'invoiceNumber', filter: { filterType: 'text', type: 'contains', filter: filters.search } },
        { column: 'allRecipients', filter: { filterType: 'text', type: 'contains', filter: filters.search } },
      ];
      if (matchingReasonCodes.length > 0) {
        searchConditions.push({
          column: 'reasonCode',
          filter: { filterType: 'array', type: 'in', filter: matchingReasonCodes } as any,
        });
      }
      this.datasource.addExternalFilter('searchTerm', {
        operator: WhereAggregateOperator.OR,
        conditions: searchConditions,
      });
    } else {
      this.datasource.removeExternalFilter('searchTerm');
    }
  }

  private setDatasourceOrRefresh(api: GridApi): void {
    if (!this.datasourceSet) {
      api.setServerSideDatasource(this.datasource);
      this.datasourceSet = true;
    } else {
      api.refreshServerSide({ purge: true });
    }
  }

  private createExportRequestSnapshot(): TableRequestSnapshot {
    return buildRequestSnapshot(this.gridApi(), this.columnApi());
  }

  private getRetryingRowIds(): number[] {
    const api = this.gridApi();
    if (!api) return [];
    const ids: number[] = [];
    api.forEachNode((node) => {
      if (node.data?.status === FailedEmailStatus.RETRYING) {
        ids.push(node.data.id);
      }
    });
    return ids;
  }

  private refreshGridIfEnabled(): void {
    if (this.settingsData()?.refreshUnsentOnModify !== false) {
      this.refreshGrid();
    }
  }

  private refreshGrid(): void {
    const api = this.gridApi();
    if (api) {
      api.refreshServerSide({ purge: true });
    }
    this.refreshCount();
  }

  private parseRecipientString(
    recipientString: string | undefined,
    type: 'to' | 'cc' | 'bcc',
  ): { type: 'to' | 'cc' | 'bcc'; email: string }[] {
    if (!recipientString?.trim()) return [];
    return recipientString
      .replace(/[\[\]"]+/g, '')
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean)
      .map((email) => ({ type, email }));
  }

  private refreshCount(): void {
    this.datasource
      .getUndismissedCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => this.countChange.emit(count));
  }
}
